import { ReactNode } from 'react';
import { motion } from 'framer-motion';

interface CardProps {
  title?: string;
  subtitle?: string;
  image?: string;
  imageAlt?: string;
  children?: ReactNode;
  onClick?: () => void;
  hover?: boolean;
  className?: string;
  footer?: ReactNode;
}

export function Card({
  title,
  subtitle,
  image,
  imageAlt,
  children,
  onClick,
  hover = true,
  className = '',
  footer,
}: CardProps) {
  const isClickable = !!onClick;

  const cardContent = (
    <>
      {image && (
        <div className="relative overflow-hidden rounded-t-xl -mx-4 -mt-4 mb-4">
          <img
            src={image}
            alt={imageAlt || title || 'Card image'}
            className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-space-900/80 to-transparent" />
        </div>
      )}

      {(title || subtitle) && (
        <div className="mb-3">
          {title && (
            <h3 className="text-lg font-semibold text-white truncate">
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="text-sm text-gray-400 truncate">{subtitle}</p>
          )}
        </div>
      )}

      {children && <div className="text-gray-300">{children}</div>}

      {footer && (
        <div className="mt-4 pt-3 border-t border-cosmic-purple/20">
          {footer}
        </div>
      )}
    </>
  );

  const baseStyles = `
    bg-space-800/50 backdrop-blur-sm rounded-xl
    border border-cosmic-purple/20 p-4
    ${hover ? 'hover:border-cosmic-purple/40 hover:bg-space-700/50' : ''}
    ${isClickable ? 'cursor-pointer' : ''}
    transition-all duration-300
    group
    ${className}
  `;

  if (isClickable) {
    return (
      <motion.div
        whileHover={hover ? { y: -4 } : undefined}
        whileTap={{ scale: 0.98 }}
        onClick={onClick}
        className={baseStyles}
      >
        {cardContent}
      </motion.div>
    );
  }

  return <div className={baseStyles}>{cardContent}</div>;
}
